import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { ChevronUp, Maximize2, Minus, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { CHAVE_DAS_CONFIGURACOES, ROTA_DE_CONVERSAS } from "@/components/app-nav";
import { PageError, PageLoading } from "@/components/PageState";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { PanelHeader, PanelTitle } from "@/components/ui/panel";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { contarRecortes, filtrarCaixa, type RecorteDaCaixa } from "@/lib/conversas";
import { contagem } from "@/lib/plural";
import { hasPermission } from "@/lib/proposal-permissions";
import type { ConversaUnificada } from "@/lib/types";
import { cn } from "@/lib/utils";
import { camadaDeVidroClass } from "@brevi/ui-kit/superficies";

import type { JanelaDoChat } from "./chat-rapido-estado";
import { useChatRapido } from "./ChatRapidoProvider";
import { ConversaExterna } from "./ConversaExterna";
import { ConversaInterna, type JanelaDaConversa } from "./ConversaInterna";
import { ListaDaCaixa } from "./ListaDaCaixa";

/** Os ids de uma conversa no chat rápido (cabem três janelas na tela). */
export function idsDoChatRapido(id: string) {
  const k = id.replace(/\W/gu, "-");
  return { titulo: `chat-rapido-titulo-${k}`, campo: `chat-rapido-campo-${k}` };
}

function naCaixa(caminho: string): boolean {
  return caminho === ROTA_DE_CONVERSAS || caminho.startsWith(`${ROTA_DE_CONVERSAS}/`);
}

/**
 * Escolher uma conversa: fora de `/conversas`, ela abre no chat rápido; na
 * própria caixa (onde o chat rápido não fica, decisão do dono), abre nela.
 * Devolve `true` quando abriu no chat rápido.
 */
export function useAbrirConversa(): (conversa: ConversaUnificada) => boolean {
  const chat = useChatRapido();
  const { pathname } = useLocation();
  const navegar = useNavigate();
  return (conversa) => {
    if (naCaixa(pathname)) {
      void navegar({ to: ROTA_DE_CONVERSAS, search: { c: conversa.id } });
      return false;
    }
    chat?.abrir(conversa);
    return true;
  };
}

/**
 * TUT-590 — a lista do chat rápido: a mesma `ListaDaCaixa`, com a busca e os
 * recortes (Todas, Minhas, Não lidas) guardados aqui, e sem o filtro de canal,
 * o alfinete e "Nova conversa" — esses ficam na `/conversas` (decisão do dono).
 */
export function ListaDoChatRapido({
  idDoTitulo,
  acoes,
  onAbrir,
}: {
  idDoTitulo: string;
  acoes: ReactNode;
  onAbrir: (conversa: ConversaUnificada) => void;
}) {
  const chat = useChatRapido();
  const [termo, setTermo] = useState("");
  const [recorte, setRecorte] = useState<RecorteDaCaixa>("todas");
  if (!chat || chat.carregando) return <PageLoading label="Abrindo as conversas..." />;
  if (chat.falhou || !chat.caixa) {
    return <PageError message="Não foi possível carregar as conversas." onRetry={chat.tentarDeNovo} />;
  }
  const { session } = chat;
  const itens = chat.caixa.itens;
  return (
    <ListaDaCaixa
      rapida={{ idDoTitulo, acoes }}
      itens={filtrarCaixa(itens, { recorte, canais: [], busca: termo })}
      contagens={contarRecortes(itens, { canais: [], busca: termo })}
      recorte={recorte}
      onRecorte={setRecorte}
      canais={[]}
      canaisEscolhidos={[]}
      onCanal={() => undefined}
      busca={termo}
      onBusca={setTermo}
      aberta={null}
      meuId={session.user.id}
      onAbrir={(id) => {
        const conversa = itens.find((c) => c.id === id);
        if (conversa) onAbrir(conversa);
      }}
      podeFixarMais={false}
      estado={chat.caixa.externas.estado}
      podeAdministrar={
        hasPermission(session, "atendimento.administrar") && hasPermission(session, CHAVE_DAS_CONFIGURACOES)
      }
      onTentarDeNovo={chat.tentarDeNovo}
      temMais={false}
      carregandoMais={false}
      onCarregarMais={() => undefined}
      onLimpar={() => {
        setTermo("");
        setRecorte("todas");
      }}
      indisponiveis={[]}
    />
  );
}

/** A conversa no chat rápido: a mesma peça da caixa, com o topo da janela. */
function ConversaRapida({
  conversa,
  janela,
  onVoltar,
}: {
  conversa: ConversaUnificada;
  janela: JanelaDaConversa;
  onVoltar?: () => void;
}) {
  const chat = useChatRapido();
  if (!chat) return null;
  return conversa.fonte === "cw" ? (
    <ConversaExterna
      conversa={conversa}
      rascunhos={chat.rascunhos}
      respostas={null}
      processo={null}
      onMudou={chat.atualizarCaixa}
      onVoltar={onVoltar}
      janela={janela}
    />
  ) : (
    <ConversaInterna
      conversa={conversa}
      meuId={chat.session.user.id}
      rascunhos={chat.rascunhos}
      respostas={null}
      onMudou={chat.atualizarCaixa}
      onVoltar={onVoltar}
      janela={janela}
    />
  );
}

/**
 * Quando a conversa não pode abrir aqui: sem a permissão "Atender conversas"
 * (o atendimento), ou fora das recentes depois de recarregar a página.
 */
function SemConversaAqui({ id, idDoTitulo, acoes }: { id: string; idDoTitulo: string; acoes: ReactNode }) {
  const chat = useChatRapido();
  const semAcesso = id.startsWith("cw:") && chat?.caixa?.externas.estado === "SEM_PERMISSAO";
  return (
    <>
      <PanelHeader acoes={acoes}>
        <PanelTitle id={idDoTitulo}>{semAcesso ? "Sem acesso" : "Conversa"}</PanelTitle>
      </PanelHeader>
      <p className="m-0 flex-1 p-4 text-corpo text-[var(--muted)]">
        {!chat?.caixa
          ? "Carregando…"
          : semAcesso
            ? "É um atendimento de WhatsApp ou e-mail, e ele fica com quem tem a permissão “Atender conversas”."
            : "Esta conversa não está entre as recentes. Abra em tela cheia para encontrá-la."}
      </p>
    </>
  );
}

/** A conversa a mostrar, ou `null` quando a pessoa não a alcança. */
function conversaVisivel(chat: NonNullable<ReturnType<typeof useChatRapido>>, id: string) {
  if (id.startsWith("cw:") && chat.caixa?.externas.estado === "SEM_PERMISSAO") return null;
  return chat.conversaDa(id);
}

function JanelaNoCanto({ janela }: { janela: JanelaDoChat }) {
  const chat = useChatRapido();
  const navegar = useNavigate();
  if (!chat) return null;
  const conversa = conversaVisivel(chat, janela.id);
  const ids = idsDoChatRapido(janela.id);
  const titulo = conversa?.titulo ?? "Conversa";
  const naoLidas = conversa?.naoLidas ?? 0;
  const telaCheia = (
    <IconButton
      tamanho="sm"
      label="Abrir em tela cheia"
      icone={<Maximize2 aria-hidden="true" />}
      onClick={() => void navegar({ to: ROTA_DE_CONVERSAS, search: { c: janela.id } })}
    />
  );
  const fechar = (
    <IconButton
      tamanho="sm"
      label={`Fechar a conversa com ${titulo}`}
      dica="Fechar"
      icone={<X aria-hidden="true" />}
      onClick={() => chat.fechar(janela.id)}
    />
  );
  const acoes = (
    <>
      <IconButton
        tamanho="sm"
        label="Minimizar"
        icone={<Minus aria-hidden="true" />}
        onClick={() => chat.alternar(janela.id)}
      />
      {telaCheia}
      {fechar}
    </>
  );
  return (
    <section
      aria-label={`Conversa com ${titulo}`}
      data-janela-do-chat={janela.id}
      className={cn(
        camadaDeVidroClass,
        "pointer-events-auto flex w-[18rem] min-w-0 flex-col overflow-hidden rounded-t-[var(--radius-bloco)] border border-b-0 shadow-[var(--shadow-md)]",
        !janela.minimizada && "h-[min(30rem,calc(100dvh-5rem))]",
      )}
    >
      {janela.minimizada ? (
        // Minimizada, vira uma barra com o nome e o contador; o clique restaura.
        <div className="flex min-h-11 items-center gap-0.5 pr-1">
          <Button
            type="button"
            variant="discreto"
            size="sm"
            aria-expanded={false}
            className="min-w-0 flex-1 justify-start"
            onClick={() => chat.alternar(janela.id)}
          >
            <ChevronUp aria-hidden="true" />
            <span className="min-w-0 truncate">{titulo}</span>
            {naoLidas > 0 ? (
              <span className="ml-auto grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-[var(--primary)] px-1.5 text-legenda font-bold text-white">
                <span aria-hidden="true">{naoLidas > 99 ? "99+" : naoLidas}</span>
                <span className="sr-only">{contagem(naoLidas, "não lida", "não lidas")}</span>
              </span>
            ) : null}
          </Button>
          {telaCheia}
          {fechar}
        </div>
      ) : conversa ? (
        <ConversaRapida conversa={conversa} janela={{ idDoTitulo: ids.titulo, idDoCampo: ids.campo, acoes }} />
      ) : (
        <SemConversaAqui id={janela.id} idDoTitulo={ids.titulo} acoes={acoes} />
      )}
    </section>
  );
}

/**
 * No celular não cabem janelas: o chat é a folha de tela cheia da opção 1 da
 * prévia — a lista, e a conversa escolhida com "Voltar" para a lista.
 */
function FolhaDoChat() {
  const chat = useChatRapido();
  const abrirConversa = useAbrirConversa();
  const navegar = useNavigate();
  const [aberta, setAberta] = useState<string | null>(null);
  if (!chat) return null;
  const conversa = aberta ? conversaVisivel(chat, aberta) : null;
  // Ir para a `/conversas` fecha a folha (ver `ChatRapido`).
  const fechar = (
    <IconButton tamanho="sm" label="Fechar o chat" dica="Fechar" icone={<X aria-hidden="true" />} onClick={() => chat.setFolhaAberta(false)} />
  );
  return (
    <Sheet open={chat.folhaAberta} onOpenChange={chat.setFolhaAberta}>
      <SheetContent
        side="right"
        showCloseButton={false}
        aria-describedby={undefined}
        className="w-full gap-0 overflow-hidden p-0 sm:max-w-none"
      >
        <SheetTitle className="sr-only">Conversas</SheetTitle>
        {aberta && conversa ? (
          <ConversaRapida
            conversa={conversa}
            onVoltar={() => setAberta(null)}
            janela={{
              idDoTitulo: idsDoChatRapido(aberta).titulo,
              idDoCampo: idsDoChatRapido(aberta).campo,
              acoes: (
                <>
                  <IconButton
                    tamanho="sm"
                    label="Abrir em tela cheia"
                    icone={<Maximize2 aria-hidden="true" />}
                    onClick={() => void navegar({ to: ROTA_DE_CONVERSAS, search: { c: aberta } })}
                  />
                  {fechar}
                </>
              ),
            }}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <ListaDoChatRapido
              idDoTitulo="chat-rapido-folha"
              acoes={
                <>
                  <Button asChild variant="discreto" size="sm">
                    <Link to={ROTA_DE_CONVERSAS}>Ver todas</Link>
                  </Button>
                  {fechar}
                </>
              }
              onAbrir={(escolhida) => {
                if (abrirConversa(escolhida)) setAberta(escolhida.id);
                else chat.setFolhaAberta(false);
              }}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * TUT-590 — o chat rápido (volta atrás na TUT-338a por decisão do dono, C1):
 * as conversas por cima da tela, sem sair dela. No desktop, até três janelas
 * no canto de baixo à direita (Odoo, Gmail), cada uma minimizável; no celular,
 * a folha de tela cheia. Monta UMA vez no `AppShell`, e por isso continua
 * aberto, na mesma conversa, enquanto a pessoa navega (C3). Na `/conversas`
 * ele fecha: a caixa inteira já está na tela.
 */
export function ChatRapido() {
  const chat = useChatRapido();
  const celular = useIsMobile();
  const { pathname } = useLocation();
  const chegouNaCaixa = naCaixa(pathname);
  const fecharTodas = chat?.fecharTodas;
  const setFolhaAberta = chat?.setFolhaAberta;
  useEffect(() => {
    if (!chegouNaCaixa) return;
    fecharTodas?.();
    setFolhaAberta?.(false);
    // Só na chegada: as funções do contexto mudam a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chegouNaCaixa]);
  if (!chat) return null;
  if (celular) return <FolhaDoChat />;
  if (!chat.janelas.length) return null;
  return (
    // Acima do conteúdo e do cabeçalho (z-30), abaixo dos diálogos (z-50) e
    // do aviso "Desfazer" do Kanban (z-80).
    <div
      data-chat-rapido=""
      className="pointer-events-none fixed bottom-0 right-4 z-40 flex max-w-[calc(100vw-2rem)] flex-row-reverse items-end gap-3"
    >
      {chat.janelas.map((janela) => (
        <JanelaNoCanto key={janela.id} janela={janela} />
      ))}
    </div>
  );
}
